import type * as React from 'react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { cn } from '@/lib/utils';

/**
 * A table always scrolls inside its own container rather than widening the
 * page. On a phone the page body must never scroll sideways.
 *
 * `busy` is for a refresh that keeps the current rows on screen: they stay
 * readable under a spinner rather than being replaced by a skeleton, which
 * would say "gone" when the answer is "being replaced". Use a skeleton when
 * there is nothing to show yet.
 */
function Table({
  className,
  busy,
  ...props
}: React.ComponentProps<'table'> & { busy?: boolean }) {
  return (
    <div className="relative w-full rounded-[inherit]">
      {/* Sibling of the table, not a parent, so the spinner is not dimmed
          along with the rows it is reporting on — and outside the scroll box,
          so it can stick to the visible part of a long table. */}
      <BusyOverlay show={busy} />
      <div data-slot="table-container" className="w-full overflow-x-auto">
        <table
          data-slot="table"
          aria-busy={busy}
          className={cn(
            'w-full caption-bottom text-sm',
            busy && 'opacity-60 transition-opacity',
            className
          )}
          {...props}
        />
      </div>
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('bg-muted/50 border-t font-medium', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors',
        className
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-muted-foreground h-10 px-3 text-left align-middle text-xs font-medium whitespace-nowrap',
        className
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn('px-3 py-2.5 align-middle', className)}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
